/** @internal – import from `@/store/useDepartmentStore` instead. */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { env } from "@/config/env";
import type {
  CoAgentState,
  CoAgentType,
  StepStatus,
  CampaignData,
  CoAgentMessage,
  StepLog,
  ThoughtEvent,
} from "./types";
import {
  generateId,
  DEFAULT_STEP_STATUSES,
  DEFAULT_STEP_LOGS,
  DEFAULT_THOUGHT_EVENTS,
  authHeaders,
  formatErrorDetails,
  buildErrorMessage,
  getMaxStepIndexForType,
} from "./utils";
import { createPipelineActions, abortPendingPipelines } from "./slices/pipelineActions";
import { createStepActions } from "./slices/stepActions";
import { createMessageActions } from "./slices/messageActions";
import {
  createWidgetActions,
  MAX_CONTROL_TRIGGER_KEYS,
} from "./slices/widgetActions";
import {
  PRODUCT_METRICS_READY,
  PRODUCT_GATE_NEXT_STEP_INDEX,
  CONTENT_INSPIRATION_STEP_INDEX,
  ADS_OPTIMIZATION_STEP_INDEX,
  buildProductGateContinueState,
} from "./pipelines/ecommerceHighlights";
import {
  containsBrandCampaignDataPatch,
  syncBrandConfigInCampaignData,
} from "@/features/brand/lib/brandCampaignData";
import { trackCoAgentStart } from "@/shared/lib/gtm";
import {
  getUnifiedFlowConfig,
} from "@/config/unifiedFlows";
import { wireResearchSubscriber } from "../../events/researchSubscriber";
import { wireBrandSubscriber } from "../../events/brandSubscriber";
import { wireGeoSubscriber } from "../../events/geoSubscriber";
import { wireSocialSubscriber } from "../../events/socialSubscriber";
import { wireGrowthSubscriber } from "../../events/growthSubscriber";
import { wireEcommerceSubscriber } from "../../events/ecommerceSubscriber";
import { wireEcommerceMetricsSubscriber } from "../../events/ecommerceMetricsSubscriber";
import { wireContentIntelligenceSubscriber } from "../../events/contentIntelligenceSubscriber";
import { wireSocialVideoSubscriber } from "../../events/socialVideoSubscriber";

let _brandSnapshotPersistTimer: ReturnType<typeof setTimeout> | null = null;
let _frontendStateSaveTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_PERSISTED_MESSAGES = 80;
const MAX_PERSISTED_LOGS_PER_STEP = 80;
const PRODUCT_GATE_HIGHLIGHT_ID = PRODUCT_METRICS_READY.widgetId;
const PRODUCT_GATE_CONTINUE_LABEL = PRODUCT_METRICS_READY.actionLabel;

function pruneCampaignDataForPersist(campaignData: CampaignData): CampaignData {
  const pruned: CampaignData = { ...campaignData };
  // Avoid localStorage quota blowups: keep runtime assets in-memory only.
  delete pruned.brandLogoOptions;
  delete pruned.brandIconResults;
  delete pruned.brandAccessoryAssets;
  delete pruned.brandBackgroundAssets;
  delete pruned.brandWebsiteContent;
  // brandConfig embeds research results + icon SVGs — strip it to avoid
  // duplicating the heavy data that was already pruned above.
  // It is a derived field rebuilt by syncBrandConfigInCampaignData on every update.
  delete pruned.brandConfig;
  return pruned;
}

function scheduleBrandCampaignSnapshotPersist(
  sessionId: string | null,
  campaignData: CampaignData,
) {
  if (typeof window === "undefined" || !sessionId) return;
  if (_brandSnapshotPersistTimer) {
    clearTimeout(_brandSnapshotPersistTimer);
  }

  _brandSnapshotPersistTimer = setTimeout(async () => {
    // Prune heavy binary assets (logo base64, icon SVGs, backgrounds) before
    // sending to the API — same pruning applied to localStorage persistence.
    const pruned = pruneCampaignDataForPersist(campaignData);
    fetch(`${env.coreUrl}/core/coagent/brand-state`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ sessionId, campaignData: pruned }),
    }).catch((error) => {
      console.error("[coagent/brand-state] snapshot persist failed:", error);
    });
  }, 800);
}

function pruneMessagesForPersist(messages: CoAgentMessage[]): CoAgentMessage[] {
  if (messages.length <= MAX_PERSISTED_MESSAGES) return messages;
  return messages.slice(-MAX_PERSISTED_MESSAGES);
}

function pruneStepLogsForPersist(stepLogs: Record<number, StepLog[]>): Record<number, StepLog[]> {
  const compact: Record<number, StepLog[]> = {};
  Object.entries(stepLogs).forEach(([stepIndex, logs]) => {
    if (!logs?.length) return;
    compact[Number(stepIndex)] =
      logs.length > MAX_PERSISTED_LOGS_PER_STEP ? logs.slice(-MAX_PERSISTED_LOGS_PER_STEP) : logs;
  });
  return compact;
}

function pruneThoughtEventsForPersist(
  events: Record<number, ThoughtEvent[]>,
): Record<number, ThoughtEvent[]> {
  const compact: Record<number, ThoughtEvent[]> = {};
  Object.entries(events).forEach(([stepIndex, items]) => {
    if (!items?.length) return;
    compact[Number(stepIndex)] =
      items.length > MAX_PERSISTED_LOGS_PER_STEP
        ? items.slice(-MAX_PERSISTED_LOGS_PER_STEP)
        : items;
  });
  return compact;
}

function normalizeMessageTimestamp(value: unknown): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

function buildPersistedHighlightAction(
  highlight: Pick<CoAgentState["widgetHighlights"][number], "widgetId" | "actionLabel">,
): (() => void) | undefined {
  if (highlight.widgetId !== PRODUCT_GATE_HIGHLIGHT_ID) return undefined;
  if ((highlight.actionLabel ?? "") !== PRODUCT_GATE_CONTINUE_LABEL) return undefined;

  return () => {
    const state = useDepartmentStore.getState();
    state.clearWidgetHighlight(PRODUCT_GATE_HIGHLIGHT_ID);
    useDepartmentStore.setState((current) => buildProductGateContinueState(current));
    useDepartmentStore.getState().setFocusedStep(PRODUCT_GATE_NEXT_STEP_INDEX);
  };
}

function restoreWidgetHighlights(
  value: unknown,
  fallback: CoAgentState["widgetHighlights"],
): CoAgentState["widgetHighlights"] {
  if (!Array.isArray(value)) return fallback;

  const restored: CoAgentState["widgetHighlights"] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const candidate = item as {
      widgetId?: unknown;
      comment?: unknown;
      expiresAt?: unknown;
      actionLabel?: unknown;
      stepIndex?: unknown;
    };
    if (typeof candidate.widgetId !== "string" || !candidate.widgetId.trim()) continue;
    if (typeof candidate.comment !== "string") continue;
    if (typeof candidate.expiresAt !== "number" || !Number.isFinite(candidate.expiresAt)) continue;

    const highlight: CoAgentState["widgetHighlights"][number] = {
      widgetId: candidate.widgetId,
      comment: candidate.comment,
      expiresAt: candidate.expiresAt,
      actionLabel: typeof candidate.actionLabel === "string" ? candidate.actionLabel : undefined,
      stepIndex: typeof candidate.stepIndex === "number" ? candidate.stepIndex : undefined,
    };
    const onAction = buildPersistedHighlightAction(highlight);
    if (onAction) highlight.onAction = onAction;
    restored.push(highlight);
  }
  return restored;
}

function hasEcommerceContentInspirationData(campaignData: CampaignData | undefined): boolean {
  if (!campaignData) return false;
  if (
    campaignData.contentIntelligenceStatus === "running" ||
    campaignData.contentIntelligenceStatus === "complete" ||
    campaignData.contentIntelligenceStatus === "error"
  ) {
    return true;
  }
  return Boolean(
    (campaignData.tiktokTopAds?.length ?? 0) > 0 ||
    (campaignData.videoAnalyses?.length ?? 0) > 0 ||
    (campaignData.contentBriefs?.length ?? 0) > 0,
  );
}

function remapLegacyEcommerce10StepIndex(index: number): number {
  if (index <= 0) return 0;
  if (index <= 3) return 1;
  if (index <= 5) return 2;
  if (index <= 6) return CONTENT_INSPIRATION_STEP_INDEX;
  return ADS_OPTIMIZATION_STEP_INDEX;
}

function remapLegacyEcommerce4StepIndex(
  index: number,
  campaignData: CampaignData | undefined,
): number {
  if (index <= 2) return index;
  if (index === 3) {
    return hasEcommerceContentInspirationData(campaignData)
      ? CONTENT_INSPIRATION_STEP_INDEX
      : ADS_OPTIMIZATION_STEP_INDEX;
  }
  return Math.min(index, ADS_OPTIMIZATION_STEP_INDEX);
}

function rebuildEcommerceStepStatuses(
  currentStepIndex: number,
  statusAtCurrent: StepStatus | undefined,
): Record<number, StepStatus> {
  const statuses: Record<number, StepStatus> = {};
  for (let i = 0; i <= ADS_OPTIMIZATION_STEP_INDEX; i += 1) {
    if (i < currentStepIndex) statuses[i] = "completed";
    else if (i === currentStepIndex) statuses[i] = statusAtCurrent || "completed";
    else statuses[i] = "pending";
  }
  return statuses;
}

function normalizeLegacyEcommerceStepState(
  currentStepIndex: number,
  stepStatuses: Record<number, StepStatus>,
  focusedStepIndex: number | null | undefined,
  campaignData: CampaignData | undefined,
): {
  currentStepIndex: number;
  stepStatuses: Record<number, StepStatus>;
  focusedStepIndex: number | null | undefined;
} {
  const hasStep4 = Object.prototype.hasOwnProperty.call(stepStatuses, ADS_OPTIMIZATION_STEP_INDEX);

  // Legacy 10-step sessions previously remapped to 5-step indices.
  if (currentStepIndex > ADS_OPTIMIZATION_STEP_INDEX) {
    const old = currentStepIndex;
    const remappedCurrent = remapLegacyEcommerce10StepIndex(old);
    const remappedFocused =
      focusedStepIndex != null && focusedStepIndex > ADS_OPTIMIZATION_STEP_INDEX
        ? remapLegacyEcommerce10StepIndex(focusedStepIndex)
        : focusedStepIndex;
    return {
      currentStepIndex: remappedCurrent,
      stepStatuses: rebuildEcommerceStepStatuses(remappedCurrent, stepStatuses[old]),
      focusedStepIndex: remappedFocused,
    };
  }

  // Legacy 4-step sessions (Store/Products/Plan/Ads) need index shifting.
  if (!hasStep4) {
    const old = currentStepIndex;
    const remappedCurrent = remapLegacyEcommerce4StepIndex(old, campaignData);
    const remappedFocused =
      focusedStepIndex != null
        ? remapLegacyEcommerce4StepIndex(focusedStepIndex, campaignData)
        : focusedStepIndex;
    return {
      currentStepIndex: remappedCurrent,
      stepStatuses: rebuildEcommerceStepStatuses(remappedCurrent, stepStatuses[old]),
      focusedStepIndex: remappedFocused,
    };
  }

  return { currentStepIndex, stepStatuses, focusedStepIndex };
}

export const useDepartmentStore = create<CoAgentState>()(
  persist(
    (set, get) => ({
      sessionId: null,
      chatId: null,
      isInitializing: false,
      error: null,

      coAgentType: "ads" as CoAgentType,

      // Google Ads integration
      isGAdsConnected: false,
      gAdsStatus: null,
      gAdsCustomerId: null,
      showGAdsModal: false,
      isMetaConnected: false,
      metaAccountName: null,
      isTikTokConnected: false,
      tikTokAccountName: null,

      // Companion interaction mode & widget highlights
      companionMode: "chat",
      copilotGreetingSent: false,
      sentControlTriggerKeys: [],
      widgetHighlights: [],

      // Widget context inspector
      attachedWidgetContexts: [],
      sentMessageContexts: {},

      hasStarted: false,
      selectedAgentId: null,
      agentStyle: null,
      campaignName: "",

      currentStepIndex: 0,
      lastBriefedStep: -1,
      focusedStepIndex: null,
      stepStatuses: { ...DEFAULT_STEP_STATUSES },
      stepLogs: { ...DEFAULT_STEP_LOGS },
      thoughtEvents: { ...DEFAULT_THOUGHT_EVENTS },

      messages: [],

      isStepLoading: false,
      stepError: null,

      campaignData: {},

      initializeWorkspace: async (agentId, agentStyle, name, coAgentType = "ads", workspaceId) => {
        set({ isInitializing: true, error: null });

        try {
          // Skillflows-prune Phase 2/2.E — every workflow type lives in
          // UNIFIED_RUNTIME_FLOWS since 2.D.2; the legacy
          // `/core/coagent/init` fallback is gone. AgentRuntimeSession
          // via `/core/agent/start` is the only initialization path.
          const unifiedConfig = getUnifiedFlowConfig(coAgentType);
          if (!unifiedConfig) {
            throw new Error(
              `Workflow '${coAgentType}' has no unified-runtime config; cannot initialize.`,
            );
          }
          const startRes = await fetch(`${env.coreUrl}/core/agent/start`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
              agentName: unifiedConfig.agentName,
              workspaceId,
              title: name,
            }),
          });
          if (!startRes.ok) {
            const err = await startRes.json().catch(() => ({ error: "Start failed" }));
            throw new Error(
              buildErrorMessage(err.error || `Start failed (${startRes.status})`, err.details),
            );
          }
          const startData = await startRes.json();
          const sessionId = startData.sessionId as string;
          // Unified runtime has no separate chatId concept — sessionId
          // doubles as the chat handle. Downstream chatId consumers are
          // social/ads-only; brand-DNA doesn't read chatId for /chat-messages.
          const chatId: string | null = sessionId;
          const maxStepIndex = getMaxStepIndexForType(coAgentType);
          const initialStepStatuses = Object.fromEntries(
            Array.from({ length: maxStepIndex + 1 }, (_, i) => [
              i,
              i === 0 ? "running" : ("pending" as StepStatus),
            ]),
          ) as Record<number, StepStatus>;

          set({
            sessionId,
            chatId,
            coAgentType,
            hasStarted: true,
            selectedAgentId: agentId,
            agentStyle,
            campaignName: name,
            isInitializing: false,
            currentStepIndex: 0,
            lastBriefedStep: -1,
            focusedStepIndex: null,
            copilotGreetingSent: false,
            sentControlTriggerKeys: [],
            stepStatuses: initialStepStatuses,
            messages:
              coAgentType === "products"
                ? []
                : [
                    {
                      id: generateId(),
                      role: "agent",
                      content:
                        coAgentType === "ads"
                          ? "Growth Pipeline workspace initialized. Tell Marcus about your business to get started."
                          : coAgentType === "content"
                            ? "Email & Creative workspace initialized. Tell Skylar about your brand to get started."
                            : coAgentType === "brand-dna"
                              ? "Brand Designer workspace initialized. Tell Remi about your brand to get started."
                              : coAgentType === "geo"
                                ? "GEO Audit workspace initialized. Share your business URL and what you do to start the audit."
                                : "Workspace initialized. Tell your agent about your brand to get started.",
                      source: "system",
                      dedupeKey: `workspace:init:${coAgentType}`,
                      timestamp: new Date(),
                    },
                  ],
          });
          trackCoAgentStart(name, agentId);
          setTimeout(() => get().setStepWaiting(0), 1000);
          return sessionId as string;
        } catch (e: any) {
          set({ isInitializing: false, error: e.message || "Failed to initialize" });
          return null;
        }
      },

      callStep: async (step, userInput) => {
        const { sessionId, addStepLog } = get();
        if (!sessionId) {
          return { success: false, error: "No session" };
        }

        set({ isStepLoading: true, stepError: null });
        addStepLog(step, `Starting step ${step + 1}...`);

        try {
          const res = await fetch(`${env.coreUrl}/core/coagent/step`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({ sessionId, step, userInput }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: "Step failed" }));
            const detailText = formatErrorDetails(err.details);
            if (res.status === 402) {
              const message = detailText
                ? `Insufficient credits (${detailText})`
                : "Insufficient credits";
              console.error("[coagent/step] Credit error", {
                sessionId,
                step,
                details: err.details,
              });
              addStepLog(step, `[ERROR] ${message}`);
              set({ isStepLoading: false, stepError: message });
              return { success: false, error: message };
            }
            const msg = buildErrorMessage(err.error || `Step failed (${res.status})`, err.details);
            addStepLog(step, `[ERROR] ${msg}`);
            throw new Error(msg);
          }

          const result = await res.json();
          addStepLog(step, `[SUCCESS] Done — ${result.creditsUsed ?? 0} credits used`);
          set({ isStepLoading: false });
          return { success: true, data: result.data, creditsUsed: result.creditsUsed };
        } catch (e: any) {
          addStepLog(step, `[ERROR] ${e.message}`);
          set({ isStepLoading: false, stepError: e.message });
          return { success: false, error: e.message };
        }
      },

      ...createPipelineActions(set, get),

      ...createStepActions(set, get),

      ...createMessageActions(set, get),

      updateCampaignData: (data) => {
        const shouldSyncBrandConfig =
          get().coAgentType === "brand-dna" ||
          containsBrandCampaignDataPatch(data as Record<string, unknown>);

        set((state) => {
          const mergedCampaignData = { ...state.campaignData, ...data };

          return {
            campaignData: shouldSyncBrandConfig
              ? syncBrandConfigInCampaignData(mergedCampaignData, state.sessionId)
              : mergedCampaignData,
          };
        });

        if (shouldSyncBrandConfig) {
          const nextState = get();
          if (nextState.coAgentType === "brand-dna") {
            scheduleBrandCampaignSnapshotPersist(nextState.sessionId, nextState.campaignData);
          }
        }
      },

      hydrateFromServer: (data) => {
        const fs = (data.frontendState ?? {}) as Record<string, unknown>;
        set((state) => {
          const sameSession = state.sessionId === data.sessionId;
          const isEcommerce = (data.coAgentType || "ads") === "products";

          // Restore messages from frontend_state, falling back to server messages.
          // For ecommerce sessions the Zustand store messages must stay empty —
          // companion chat is driven by the AI SDK (useChat), and filling the
          // store with resume data would cause the ecommerce message-sync effect
          // to re-inject them, duplicating the opening message.
          const messageSource = isEcommerce
            ? Array.isArray(fs.messages)
              ? fs.messages
              : []
            : Array.isArray(fs.messages) && fs.messages.length > 0
              ? fs.messages
              : sameSession && state.messages.length > 0
                ? state.messages
                : data.messages || [];
          const restoredMessages = (messageSource as CoAgentMessage[]).map((m: any) => ({
            ...m,
            timestamp: normalizeMessageTimestamp(m?.timestamp),
          }));

          const fallbackTriggerKeys = sameSession
            ? state.sentControlTriggerKeys.filter((k) => k.startsWith(`${data.sessionId}:`))
            : [];

          const rawFocusedStepIndex =
            fs.focusedStepIndex !== undefined
              ? (fs.focusedStepIndex as number | null)
              : sameSession
                ? state.focusedStepIndex
                : null;
          const rawStepStatuses = (data.stepStatuses ?? {}) as Record<number, StepStatus>;
          const normalizedEcommerce =
            isEcommerce &&
            typeof data.currentStepIndex === "number" &&
            rawStepStatuses &&
            typeof rawStepStatuses === "object"
              ? normalizeLegacyEcommerceStepState(
                  data.currentStepIndex,
                  rawStepStatuses,
                  rawFocusedStepIndex,
                  data.campaignData as CampaignData | undefined,
                )
              : null;

          return {
            sessionId: data.sessionId,
            chatId: data.chatId,
            coAgentType: data.coAgentType || "ads",
            hasStarted: true,
            selectedAgentId: data.agentId,
            agentStyle: data.agentStyle,
            campaignName: data.campaignName,
            currentStepIndex: normalizedEcommerce?.currentStepIndex ?? data.currentStepIndex,
            stepStatuses: normalizedEcommerce?.stepStatuses ?? data.stepStatuses,
            campaignData: data.campaignData,
            // Server-persisted frontend state (survives localStorage wipes)
            copilotGreetingSent:
              typeof fs.copilotGreetingSent === "boolean"
                ? fs.copilotGreetingSent
                : sameSession
                  ? state.copilotGreetingSent
                  : false,
            lastBriefedStep:
              typeof fs.lastBriefedStep === "number"
                ? fs.lastBriefedStep
                : sameSession
                  ? state.lastBriefedStep
                  : -1,
            companionMode:
              fs.companionMode === "chat" || fs.companionMode === "voice"
                ? fs.companionMode
                : sameSession
                  ? state.companionMode
                  : "voice",
            focusedStepIndex: normalizedEcommerce?.focusedStepIndex ?? rawFocusedStepIndex,
            sentControlTriggerKeys: Array.isArray(fs.sentControlTriggerKeys)
              ? (fs.sentControlTriggerKeys as string[]).filter((k: string) =>
                  k.startsWith(`${data.sessionId}:`),
                )
              : fallbackTriggerKeys,
            widgetHighlights: restoreWidgetHighlights(
              fs.widgetHighlights,
              sameSession ? state.widgetHighlights : [],
            ),
            messages: restoredMessages,
            stepLogs:
              fs.stepLogs && typeof fs.stepLogs === "object"
                ? (fs.stepLogs as CoAgentState["stepLogs"])
                : sameSession
                  ? state.stepLogs
                  : { ...DEFAULT_STEP_LOGS },
            thoughtEvents:
              fs.thoughtEvents && typeof fs.thoughtEvents === "object"
                ? (fs.thoughtEvents as CoAgentState["thoughtEvents"])
                : sameSession
                  ? state.thoughtEvents
                  : { ...DEFAULT_THOUGHT_EVENTS },
          };
        });
      },

      hydrateArtifactsOnly: (data) => {
        // Mid-conversation refresh — refresh only artifact-derived data
        // and leave step state alone. Step progression is the agent's
        // (advance_step) domain; letting a server hydrate update
        // currentStepIndex while a stream is in flight races advance_step
        // and silently marks the NEXT step complete (e.g. brand-strategy
        // gets a checkmark with no agent ever running).
        set((state) => {
          if (state.sessionId !== data.sessionId) return state; // session drift — bail
          // Don't downgrade campaignData if local has newer keys (the
          // chat may have already merged tool results client-side).
          // Server merge: spread server first, then local overrides
          // anything local has explicitly populated.
          const merged = {
            ...(data.campaignData ?? {}),
            ...state.campaignData,
            // ALWAYS take server's values for keys the artifact path is
            // authoritative for (the latest synthesis / distillations).
            ...(data.campaignData?.brandSynthesis !== undefined && {
              brandSynthesis: data.campaignData.brandSynthesis,
            }),
            ...(data.campaignData?.brandPerplexityResult !== undefined && {
              brandPerplexityResult: data.campaignData.brandPerplexityResult,
            }),
            ...(data.campaignData?.brandTwitterResult !== undefined && {
              brandTwitterResult: data.campaignData.brandTwitterResult,
            }),
            ...(data.campaignData?.brandYoutubeResult !== undefined && {
              brandYoutubeResult: data.campaignData.brandYoutubeResult,
            }),
            ...(data.campaignData?.brandGoogleSearchResult !== undefined && {
              brandGoogleSearchResult: data.campaignData.brandGoogleSearchResult,
            }),
            ...(data.campaignData?.brandPopulateResult !== undefined && {
              brandPopulateResult: data.campaignData.brandPopulateResult,
            }),
            ...(data.campaignData?.brandCoAgentInput !== undefined && {
              brandCoAgentInput: data.campaignData.brandCoAgentInput,
            }),
          };
          return { campaignData: merged as CampaignData };
        });
      },

      reset: () => {
        abortPendingPipelines();
        set({
          sessionId: null,
          chatId: null,
          isInitializing: false,
          error: null,
          coAgentType: "ads" as CoAgentType,
          hasStarted: false,
          selectedAgentId: null,
          agentStyle: null,
          campaignName: "",
          currentStepIndex: 0,
          lastBriefedStep: -1,
          focusedStepIndex: null,
          stepStatuses: { ...DEFAULT_STEP_STATUSES },
          stepLogs: { ...DEFAULT_STEP_LOGS },
          thoughtEvents: { ...DEFAULT_THOUGHT_EVENTS },
          messages: [],
          isStepLoading: false,
          stepError: null,
          campaignData: {} as CampaignData,
          showGAdsModal: false,
          companionMode: "chat",
          copilotGreetingSent: false,
          sentControlTriggerKeys: [],
          widgetHighlights: [],
          attachedWidgetContexts: [],
          sentMessageContexts: {},
        });
      },
      setFocusedStep: (index) => {
        if (index === null) {
          set({ focusedStepIndex: null });
          return;
        }

        const { coAgentType } = get();
        const maxStep = getMaxStepIndexForType(coAgentType);
        if (index < 0 || index > maxStep) return;
        set({ focusedStepIndex: index });
      },
      goBack: () => {
        const state = get();
        const startIndex = state.focusedStepIndex ?? state.currentStepIndex;
        set({ focusedStepIndex: Math.max(0, startIndex - 1) });
      },
      goNext: () => {
        const state = get();
        const maxStep = getMaxStepIndexForType(state.coAgentType);
        const startIndex = state.focusedStepIndex ?? state.currentStepIndex;
        set({ focusedStepIndex: Math.min(maxStep, startIndex + 1) });
      },

      // Google Ads
      fetchGAdsStatus: async () => {
        try {
          const res = await fetch("/gads/status", { headers: await authHeaders() });
          if (!res.ok) return;
          const data = await res.json();
          set({
            isGAdsConnected: !!data.connected,
            gAdsStatus: data.status,
            gAdsCustomerId: data.customerId,
          });
        } catch {
          // Silently fail — non-critical
        }
      },
      setShowGAdsModal: (show: boolean) => set({ showGAdsModal: show }),
      requireGAdsConnection: () => {
        const state = get();
        if (state.isGAdsConnected) return true;
        set({ showGAdsModal: true });
        return false;
      },

      // Meta / TikTok
      fetchAdPlatformStatuses: async () => {
        try {
          const [metaRes, tiktokRes] = await Promise.all([
            fetch(`${env.apiUrl}/api/ad-platforms/meta/status/`, { headers: await authHeaders() }),
            fetch(`${env.apiUrl}/api/ad-platforms/tiktok/status/`, {
              headers: await authHeaders(),
            }),
          ]);
          if (metaRes.ok) {
            const meta = await metaRes.json();
            set({ isMetaConnected: !!meta.connected, metaAccountName: meta.accountName ?? null });
          }
          if (tiktokRes.ok) {
            const tt = await tiktokRes.json();
            set({ isTikTokConnected: !!tt.connected, tikTokAccountName: tt.accountName ?? null });
          }
        } catch {
          // Non-critical — ad cost tab will show connect buttons
        }
      },
      connectAdPlatform: async (platform: "meta" | "tiktok") => {
        try {
          const returnTo = `${window.location.pathname}${window.location.search}`;
          const res = await fetch(
            `${env.apiUrl}/api/ad-platforms/${platform}/auth/?returnTo=${encodeURIComponent(returnTo)}`,
            { headers: await authHeaders() },
          );
          if (!res.ok) return;
          const { url } = await res.json();
          window.location.href = url;
        } catch {
          // Silently fail
        }
      },

      disconnectAdPlatform: async (platform: "meta" | "tiktok") => {
        try {
          const res = await fetch(`${env.apiUrl}/api/ad-platforms/${platform}/disconnect/`, {
            method: "POST",
            headers: await authHeaders(),
          });
          if (!res.ok) return;
          if (platform === "meta") {
            set({ isMetaConnected: false, metaAccountName: null });
          } else {
            set({ isTikTokConnected: false, tikTokAccountName: null });
          }
        } catch {
          // Silently fail
        }
      },

      ...createWidgetActions(set, get),
    }),
    {
      name: "coagent-store",
      partialize: (state) => ({
        sessionId: state.sessionId,
        chatId: state.chatId,
        coAgentType: state.coAgentType,
        hasStarted: state.hasStarted,
        currentStepIndex: state.currentStepIndex,
        lastBriefedStep: state.lastBriefedStep,
        focusedStepIndex: state.focusedStepIndex,
        stepStatuses: state.stepStatuses,
        stepLogs: pruneStepLogsForPersist(state.stepLogs),
        thoughtEvents: pruneThoughtEventsForPersist(state.thoughtEvents),
        campaignData: pruneCampaignDataForPersist(state.campaignData),
        selectedAgentId: state.selectedAgentId,
        agentStyle: state.agentStyle,
        campaignName: state.campaignName,
        copilotGreetingSent: state.copilotGreetingSent,
        sentControlTriggerKeys: state.sentControlTriggerKeys,
        messages: pruneMessagesForPersist(state.messages),
      }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<CoAgentState>) };
        // Fix Date objects that were serialized to strings in localStorage
        if (Array.isArray(merged.messages)) {
          merged.messages = merged.messages.map((msg: any) => ({
            ...msg,
            timestamp: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp),
          }));
        }
        if (merged.coAgentType === "products") {
          const normalized = normalizeLegacyEcommerceStepState(
            merged.currentStepIndex,
            merged.stepStatuses,
            merged.focusedStepIndex,
            merged.campaignData,
          );
          merged.currentStepIndex = normalized.currentStepIndex;
          merged.stepStatuses = normalized.stepStatuses;
          merged.focusedStepIndex = normalized.focusedStepIndex ?? null;
        }
        // Recover stale "running" or "waiting" steps after page reload — the SSE
        // pipeline is no longer active so the step would stay stuck in loading mode.
        if (merged.coAgentType === "products") {
          const cd = merged.campaignData;
          const ss = { ...merged.stepStatuses };
          const stale = (s: string | undefined) => s === "running" || s === "waiting";
          // Step 0 (store connect): completed if we have shop info
          if (stale(ss[0]) && cd.shopInfo) ss[0] = "completed";
          // Step 1 (product discovery): completed if categories arrived
          if (stale(ss[1]) && (cd.ecommerceCategories as unknown[] | undefined)?.length)
            ss[1] = "completed";
          // Step 2 (margin analysis): completed if product metrics exist
          if (stale(ss[2]) && (cd.ecommerceProductMetrics as unknown[] | undefined)?.length)
            ss[2] = "completed";
          // Step 3 (strategy): completed if research phase is done
          if (stale(ss[3]) && cd.ecommerceResearchPhase === "done") ss[3] = "completed";
          merged.stepStatuses = ss;

          // Derive ad platform connection state from campaignData so it survives
          // page reloads (these fields aren't in partialize).
          if (cd.metaConnected) {
            merged.isMetaConnected = true;
            merged.metaAccountName = (cd.metaAccountName as string) ?? null;
          }
          if (cd.tiktokConnected) {
            merged.isTikTokConnected = true;
            merged.tikTokAccountName = (cd.tiktokAccountName as string) ?? null;
          }
        }
        return merged as CoAgentState;
      },
    },
  ),
);

// Phase 8 commits 71-79 — pipelines publish typed events on the SSE bus.
// Each subscriber is wired once at module init, lazily reading the live
// store via getState so registration order doesn't depend on
// store-internal hook timing.
wireResearchSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    updateCampaignData: s.updateCampaignData,
    addMessage: s.addMessage,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setCampaignName: (name) => useDepartmentStore.setState({ campaignName: name }),
    setFocusedStep: s.setFocusedStep,
  };
});

wireBrandSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    addThoughtEvent: s.addThoughtEvent,
    updateCampaignData: s.updateCampaignData,
    addMessage: s.addMessage,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setCampaignName: (name) => useDepartmentStore.setState({ campaignName: name }),
    setFocusedStep: s.setFocusedStep,
    isAwaitingStrategyConfirmation: () =>
      Boolean(
        useDepartmentStore.getState().campaignData
          .brandStrategyAwaitingConfirmation,
      ),
    getResearchMode: () =>
      useDepartmentStore.getState().campaignData.brandResearchMode === "refresh"
        ? "refresh"
        : "create",
  };
});

wireGeoSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    updateCampaignData: s.updateCampaignData,
    addMessage: s.addMessage,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setCampaignName: (name) => useDepartmentStore.setState({ campaignName: name }),
    setFocusedStep: s.setFocusedStep,
    getGeoInput: () => useDepartmentStore.getState().campaignData.geoInput,
  };
});

wireSocialSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    updateCampaignData: s.updateCampaignData,
    addMessage: s.addMessage,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setCampaignName: (name) => useDepartmentStore.setState({ campaignName: name }),
    setFocusedStep: s.setFocusedStep,
  };
});

wireGrowthSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    updateCampaignData: s.updateCampaignData,
    addMessage: s.addMessage,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setCampaignName: (name) => useDepartmentStore.setState({ campaignName: name }),
    setFocusedStep: s.setFocusedStep,
  };
});

wireEcommerceSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    addThoughtEvent: s.addThoughtEvent,
    addMessage: s.addMessage,
    updateCampaignData: s.updateCampaignData,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setCampaignName: (name) => useDepartmentStore.setState({ campaignName: name }),
    setFocusedStep: s.setFocusedStep,
    clearWidgetHighlight: (id) => useDepartmentStore.getState().clearWidgetHighlight(id),
    highlightWidget: (...args) => useDepartmentStore.getState().highlightWidget(...args),
    getCampaignData: () => useDepartmentStore.getState().campaignData,
    startEcommerceMetrics: () => useDepartmentStore.getState().startEcommerceMetrics(),
  };
});

wireEcommerceMetricsSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    addThoughtEvent: s.addThoughtEvent,
    updateCampaignData: s.updateCampaignData,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    getCampaignData: () => useDepartmentStore.getState().campaignData,
  };
});

wireContentIntelligenceSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    addStepLog: s.addStepLog,
    addThoughtEvent: s.addThoughtEvent,
    addMessage: s.addMessage,
    updateCampaignData: s.updateCampaignData,
    setStepState: (updater) => useDepartmentStore.setState((state) => updater(state)),
    setFocusedStep: s.setFocusedStep,
  };
});

wireSocialVideoSubscriber(() => {
  const s = useDepartmentStore.getState();
  return {
    updateCampaignData: s.updateCampaignData,
    addMessage: s.addMessage,
    getCampaignData: () => useDepartmentStore.getState().campaignData,
  };
});

// ── Debounced save of frontend state to Django ─────────────────────────────
// Persists UI-only state (greeting flags, highlights, step logs, etc.) to
// Session.frontend_state so it survives OAuth redirects and page reloads
// even when localStorage is cleared by AgentHubSelection.reset().

const FRONTEND_STATE_SAVE_DEBOUNCE_MS = 800;

/** Extract the fields we persist to Django (strip non-serializable callbacks). */
function extractFrontendState(state: CoAgentState) {
  return {
    copilotGreetingSent: state.copilotGreetingSent,
    lastBriefedStep: state.lastBriefedStep,
    companionMode: state.companionMode,
    focusedStepIndex: state.focusedStepIndex,
    sentControlTriggerKeys: state.sentControlTriggerKeys.slice(-MAX_CONTROL_TRIGGER_KEYS),
    widgetHighlights: state.widgetHighlights.map(({ onAction: _, ...rest }) => rest),
    messages: pruneMessagesForPersist(state.messages),
    stepLogs: pruneStepLogsForPersist(state.stepLogs),
    thoughtEvents: pruneThoughtEventsForPersist(state.thoughtEvents),
  };
}

/** Fields to watch — if any of these change, schedule a save. */
const _WATCHED_KEYS = [
  "copilotGreetingSent",
  "lastBriefedStep",
  "companionMode",
  "focusedStepIndex",
  "sentControlTriggerKeys",
  "widgetHighlights",
  "messages",
  "stepLogs",
  "thoughtEvents",
] as const;

let _lastSavedSnapshot = "";

function scheduleFrontendStateSave() {
  if (_frontendStateSaveTimer) clearTimeout(_frontendStateSaveTimer);
  _frontendStateSaveTimer = setTimeout(async () => {
    _frontendStateSaveTimer = null;
    const state = useDepartmentStore.getState();
    if (!state.sessionId || !state.hasStarted) return;

    const payload = extractFrontendState(state);
    const snapshot = JSON.stringify({ sessionId: state.sessionId, payload });
    if (snapshot === _lastSavedSnapshot) return; // No changes

    try {
      const headers = await authHeaders();
      // Skillflows-prune Phase 2/2.E — every flow writes ui_state via
      // `/core/agent/save-ui-state`; the legacy `save-frontend-state`
      // endpoint and skillflows.Session.frontend_state column are gone.
      const res = await fetch(`${env.coreUrl}/core/agent/save-ui-state`, {
        method: "POST",
        headers,
        body: JSON.stringify({ sessionId: state.sessionId, uiState: payload }),
      });
      if (!res.ok) return;
      _lastSavedSnapshot = snapshot;
    } catch {
      // Ignore auth header failures (not logged in)
    }
  }, FRONTEND_STATE_SAVE_DEBOUNCE_MS);
}

// Subscribe to store changes — only trigger save when watched fields change
useDepartmentStore.subscribe((state, prevState) => {
  if (!state.sessionId || !state.hasStarted) return;
  const changed = _WATCHED_KEYS.some((k) => state[k] !== prevState[k]);
  if (changed) scheduleFrontendStateSave();
});

/**
 * Immediately flush any pending frontend-state save.
 * Call before navigations that leave the SPA (e.g. OAuth redirects)
 * to ensure trigger-dedupe keys are persisted to the server.
 */
export async function flushFrontendStateSave(): Promise<void> {
  if (_frontendStateSaveTimer) {
    clearTimeout(_frontendStateSaveTimer);
    _frontendStateSaveTimer = null;
  }
  const state = useDepartmentStore.getState();
  if (!state.sessionId || !state.hasStarted) return;

  const payload = extractFrontendState(state);
  const snapshot = JSON.stringify({ sessionId: state.sessionId, payload });
  if (snapshot === _lastSavedSnapshot) return;

  try {
    const headers = await authHeaders();
    // Phase 2/2.E — same as the debounced path above. Unified-only.
    const res = await fetch(`${env.coreUrl}/core/agent/save-ui-state`, {
      method: "POST",
      headers,
      body: JSON.stringify({ sessionId: state.sessionId, uiState: payload }),
    });
    if (res.ok) _lastSavedSnapshot = snapshot;
  } catch {
    // Best-effort — localStorage still acts as fallback
  }
}
